import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {useCorrectEmail} from '@/hooks/platform/use-access'
import {tope} from '@/lib/campos-texto'
import {publicarExito} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'
import {validateEmail} from '@/lib/validation'

/** La persona se deduplica por cédula: borrar y recrear el empleado no cambia su correo. Un solo
 * campo a propósito: lo demás de la identidad es de la persona. */
export function CorrectEmailDialog({
  businessId,
  employeeId,
  email,
}: {
  businessId: string
  employeeId: string
  email: string | null
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(email ?? '')
  const [fieldError, setFieldError] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const correct = useCorrectEmail(businessId)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    setError(null)
    setFieldError(undefined)
    if (next) setValue(email ?? '')
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const invalid = validateEmail(value)
    setFieldError(invalid ?? undefined)
    if (invalid) return
    setError(null)
    correct.mutate(
      {employeeId, email: value.trim()},
      {
        onSuccess: (data) => {
          publicarExito(
            t('access.correctEmail.doneTitle'),
            data.invite_resent
              ? t('access.correctEmail.doneResent', {email: data.employee.email ?? value.trim()})
              : t('access.correctEmail.doneOnly'),
            {testId: 'correct-email-done'}
          )
          handleOpenChange(false)
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto px-1.5 py-0.5"
          data-testid="correct-email-open">
          {t('access.correctEmail.action')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('access.correctEmail.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('access.correctEmail.description')}</p>
          <div className="space-y-2">
            <Label htmlFor="correct-email">{t('employees.fields.email')}</Label>
            <Input
              id="correct-email"
              data-testid="correct-email-input"
              inputMode="email"
              autoComplete="email"
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                setFieldError(undefined)
              }}
              onBlur={() => setFieldError(validateEmail(value) ?? undefined)}
              maxLength={tope('MAIL')}
              aria-invalid={!!fieldError}
            />
            <FieldError error={fieldError} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" data-testid="correct-email-save" disabled={correct.isPending}>
              {t('access.correctEmail.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
